import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Typography,
  Paper,
  TextField,
  Box,
  Grid,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSearchParams } from "react-router-dom";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import DoDisturbAltIcon from "@mui/icons-material/DoDisturbAlt";
import PreviewPanel from "../helpers/components/PreviewPanel";
import {
  setFilteringDefJSONOutput,
  setIsValidRowFiltering,
  setRowFilteringDataForPayload,
} from "../../../../../features/uniqueDefSlice";
import { useDispatch } from "react-redux";
import { useFilterObjValidation } from "../helpers/hooks/useFilteringObjValidation";
interface FilterRow {
  id: number;
  fieldName: string;
  value: string;
  condition: string;
}

const CreateNewDefinitionRowFiltering = ({ setShowLookupFields }: any) => {
  const [searchParams] = useSearchParams();
  const interfaceName = searchParams.get("interfaceName");
  const destinationTableName = searchParams.get("destinationTableName");

  const dispatch = useDispatch();
  const apiRef = React.useRef<any>(null);
  const [activityFilters, setActivityFilters] = useState([
    { id: 1, field: "", format: "" },
  ]);

  const addActivityFilter = () => {
    const newId = Math.max(...activityFilters.map((f) => f.id), 0) + 1;
    setActivityFilters((prev) => [
      ...prev,
      { id: newId, field: "", format: "" },
    ]);
  };

  const removeActivityFilter = (id: number) => {
    // never remove the first (primary) filter; allow only additional ones to be removed
    if (id === activityFilters[0].id) return;
    setActivityFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const updateActivityFilter = (id: number, patch: any) => {
    setActivityFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
    );
  };
  const [filterRows, setFilterRows] = useState<FilterRow[]>([
    { id: 1, fieldName: "does_exist", value: "", condition: "" },
  ]);
  const conditionOptions = ["Equal", "Not Equal", "Contains"];

  // map condition -> icon component
  const conditionIconMap: Record<string, JSX.Element> = {
    Equal: <FormatQuoteIcon fontSize="small" sx={{ color: "#2196f3" }} />,
    "Not Equal": (
      <DoDisturbAltIcon fontSize="small" sx={{ color: "#f44336" }} />
    ),
    Contains: <DragHandleIcon fontSize="small" sx={{ color: "#4caf50" }} />,
  };

  const addFilterRow = () => {
    const newId = Math.max(...filterRows.map((row) => row.id), 0) + 1; // Added fallback for empty array
    const newRow: FilterRow = {
      id: newId,
      fieldName: "",
      value: "",
      condition: "",
    };
    setFilterRows((prev) => [...prev, newRow]);
  };

  const removeFilterRow = (id: number | string) => {
    const numericId = Number(id); // coerce to number
    if (Number.isNaN(numericId)) {
      // guard: if id is invalid, do nothing (or log)
      console.warn("removeFilterRow called with invalid id:", id);
      return;
    }
    const row = filterRows.find((r) => r.id === numericId);
    if (row?.fieldName === "does_exist") {
      console.warn("Attempt to remove protected row 'does_exist' prevented.");
      return;
    }
    setFilterRows((prev) => prev.filter((row) => row.id !== numericId));
  };

  // inside your component, before columns definition
  const EditConditionSelect = (props: any) => {
    const { id, field, value, api } = props;
    const [open, setOpen] = useState(false);
    const current = String(value ?? "");

    useEffect(() => {
      // open after mount — small timeout helps in some MUI versions
      const t = setTimeout(() => setOpen(true), 0);
      return () => clearTimeout(t);
    }, []);

    const handleChange = (e: any) => {
      const newVal = e.target.value;
      // set edit buffer
      api.setEditCellValue({ id, field, value: newVal }, e);
      // commit and stop edit mode (closes editor)
      api.stopCellEditMode({ id, field });
    };

    return (
      <Select
        value={current}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        autoFocus
        onChange={handleChange}
        size="small"
        sx={{
          width: "100%",
          // 👇 removes the default outline + radius
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none !important",
          },
          "&.MuiOutlinedInput-root": {
            borderRadius: 0,
          },
        }}
      >
        {conditionOptions.map((opt) => (
          <MenuItem key={opt} value={opt}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {conditionIconMap[opt]}
              <Typography variant="body2">{opt}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    );
  };

  // then use it as the renderEditCell:
  const renderConditionEditCell = (params: any) => {
    return <EditConditionSelect {...params} />;
  };

  const columns: GridColDef[] = [
    {
      field: "fieldName",
      headerName: "Field Name In Table",
      flex: 2,
      editable: true,
      renderCell: (params: any) => {
        const value = params.value || "";
        return (
          <Typography
            variant="body2"
            sx={{
              color: value ? "text.primary" : "text.disabled",
              fontStyle: value,
              lineHeight: 1.5,
              py: 1,
            }}
          >
            {value || "Set field name in table"}
          </Typography>
        );
      },
    },
    {
      field: "value",
      headerName: "Value",
      flex: 1,
      editable: true,
      renderCell: (params: any) => {
        const value = params.value || "";
        return (
          <Typography
            variant="body2"
            sx={{
              color: value ? "text.primary" : "text.disabled",
              fontStyle: value,
              lineHeight: 1.5,
              py: 1,
            }}
          >
            {value || "Set filtering value"}
          </Typography>
        );
      },
    },
    {
      field: "condition",
      headerName: "Condition",
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueOptions: conditionOptions,
      // render an icon + label for the cell display
      renderCell: (params: any) => {
        const val = String(params.value ?? "");
        const Icon = conditionIconMap[val];
        return (
          <>
            {val && val ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {Icon}
                  <Typography variant="body2" sx={{ lineHeight: 1 }}>
                    {val}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: val ? "text.primary" : "text.disabled",
                  fontStyle: val,
                  lineHeight: 1.5,
                  py: 1,
                }}
              >
                {"Set filtering condition"}
              </Typography>
            )}
          </>
        );
      },
      renderEditCell: renderConditionEditCell,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 80,
      getActions: ({ id }: { id: GridRowId }) => [
        <GridActionsCellItem
          key={`delete-${id}`}
          icon={<DeleteIcon color="secondary" />}
          label="Delete"
          onClick={() => removeFilterRow(id)} // pass id (coerced inside removeFilterRow)
          showInMenu={false}
        />,
      ],
    },
  ];
  const processRowUpdate = (newRow: any) => {
    const updated: FilterRow = {
      id: Number(newRow.id),
      fieldName: newRow.fieldName || "", // Keep original value if it exists
      value: newRow.value || "", // Keep original value if it exists
      condition: newRow.condition || "", // Default to "Equal" if empty
    };

    setFilterRows((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r))
    );
    return updated;
  };

  const handleProcessRowUpdateError = (error: any) => {
    console.error("Row update error:", error);
  };

  const previewObject = useMemo(() => {
    const filteringDef: Record<string, any> = {};

    // build the activity_date entry: object when single row, array when multiple
    const activityItems = activityFilters.map((af) => {
      const formats =
        typeof af.format === "string" && af.format.length
          ? af.format
              .split(",")
              .map((s: string) => s.trim())
              .filter(Boolean)
          : [];
      return {
        field: af.field || "",
        raw_table_format: formats,
      };
    });

    if (activityItems.length === 1) {
      // single object (not wrapped in an array)
      filteringDef["activity_date"] = activityItems[0];
    } else {
      // multiple -> keep as array
      filteringDef["activity_date"] = activityItems;
    }

    for (const row of filterRows) {
      const key = row.fieldName || "";
      const valueArr = Array.isArray(row.value)
        ? row.value
        : typeof row.value === "string" && row.value.length
        ? [row.value.trim()] // keep whole string as single element, trimmed
        : [];

      filteringDef[key] = {
        value: valueArr,
        condition: (row.condition || "").toLowerCase(),
      };
    }

    return filteringDef;
  }, [activityFilters, filterRows]);

  // dispatch only when previewObject (or previewJSON) changes
  const [, setDisplayedPreview] = useState<any>({});
  const [displayedPreviewJSON, setDisplayedPreviewJSON] = useState<string>(
    () => JSON.stringify({}, null, 2) // -> "{}"
  );

  // IMPORTANT: we only dispatch when the user clicks Refresh
  const handleRefreshPreview = () => {
    setDisplayedPreview(previewObject);
    const jsonStr = JSON.stringify(previewObject, null, 2);
    setDisplayedPreviewJSON(jsonStr);
    dispatch(setFilteringDefJSONOutput(previewObject));
    const formatted = JSON.stringify(previewObject).replace(/"/g, '"');
    dispatch(setRowFilteringDataForPayload(`{${formatted.slice(1, -1)}}`));
  };
  const refiltering = () => {
    handleRefreshPreview();
    setShowLookupFields(true);
  };
  const handleCellClick = (params: any) => {
    try {
      const isEditable = Boolean(params.isEditable);
      const getCellMode = apiRef.current?.getCellMode;
      const startEdit = apiRef.current?.startCellEditMode;
      if (!isEditable || !getCellMode || !startEdit) return;

      if (getCellMode(params.id, params.field) === "view") {
        startEdit({ id: params.id, field: params.field });
      }
    } catch (err) {
      console.warn("startCellEditMode error:", err);
    }
  };
  const validationResult = useFilterObjValidation(previewObject);
  dispatch(setIsValidRowFiltering(validationResult.valid));
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
          <Typography variant="h5" component="h1" sx={{ mr: 2 }}>
            Create New Definition
          </Typography>
          <Typography variant="body2" color="text.primary">
            <Box component="span" sx={{ fontWeight: 500 }}>
              Interface:
            </Box>{" "}
            {interfaceName} &nbsp;&nbsp;
            <Box component="span" sx={{ fontWeight: 500 }}>
              Destination Table:
            </Box>{" "}
            {destinationTableName}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Set the table rows the unique definition refers to, then define the
          lookup fields.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Row Filtering */}
        <Grid item xs={12} lg={6}>
          <Box sx={{ height: "fit-content" }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Row Filtering - Activity Date
            </Typography>

            {/* Activity Date Field */}
            <Box
              sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {activityFilters.map((af, idx) => {
                const isPrimary = idx === 0;
                return (
                  <Box
                    key={af.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      "& .MuiFormHelperText-root": { mt: "3px" },
                    }}
                  >
                    <TextField
                      label={isPrimary ? "Field" : "Additional Field Name"}
                      value={af.field}
                      onChange={(e) =>
                        updateActivityFilter(af.id, { field: e.target.value })
                      }
                      size="small"
                      helperText={isPrimary ? "This field is mandatory" : ""}
                      sx={{ flex: 1 }}
                      placeholder={
                        isPrimary
                          ? "Name in relevant date field in raw table"
                          : "Additional field name"
                      }
                      color="secondary"
                    />
                    <TextField
                      label={
                        isPrimary
                          ? "Raw Table Format"
                          : "Additional Field Format"
                      }
                      value={af.format}
                      onChange={(e) =>
                        updateActivityFilter(af.id, { format: e.target.value })
                      }
                      size="small"
                      helperText={isPrimary ? "This field is mandatory" : ""}
                      sx={{ flex: 1 }}
                      placeholder={
                        isPrimary
                          ? "Format of date field in raw table"
                          : "Additional field format"
                      }
                      color="secondary"
                    />

                    {/* show delete only for additional rows */}
                    {!isPrimary && (
                      <IconButton
                        onClick={() => removeActivityFilter(af.id)}
                        aria-label="delete-activity-filter"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" color="secondary" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}

              {/* ADD FILTER (activity) */}
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ whiteSpace: "nowrap" }}
                  color="secondary"
                  onClick={addActivityFilter}
                >
                  ADD FILTER
                </Button>
              </Box>
            </Box>

            {/* Other Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Row Filtering - Other Settings
            </Typography>

            <Box sx={{ width: "100%", mb: 3 }}>
              <DataGrid
                density="compact"
                rows={filterRows}
                columns={columns}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                apiRef={apiRef}
                hideFooter
                disableRowSelectionOnClick
                disableColumnMenu
                onCellClick={handleCellClick}
                isCellEditable={(params) => {
                  if (
                    params.field === "fieldName" &&
                    params.row.fieldName === "does_exist"
                  ) {
                    return false;
                  }
                  return true;
                }}
                sx={{
                  border: "none",
                  ".MuiDataGrid-columnHeaders": {
                    borderBottom: "none",
                  },
                  ".MuiDataGrid-cell": {
                    borderBottom: "none",
                  },

                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f5f5f5",
                    border: "none",
                  },
                  "& .MuiDataGrid-root": {
                    border: "1px solid #e0e0e0",
                  },

                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none !important",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    {
                      outline: "none !important",
                    },

                  // 👇 highlight editable cell in edit mode
                  "& .MuiDataGrid-cell--editing": {
                    border: (theme) =>
                      `2px solid ${theme.palette.secondary.main}`,
                  },
                }}
              />
            </Box>

            {/* Add Row Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addFilterRow}
              sx={{ textTransform: "none" }}
              color="secondary"
            >
              ADD ROW
            </Button>
          </Box>
        </Grid>

        {/* Right Column - Preview */}

        {/* JSON Preview */}
        <Grid item xs={12} lg={6}>
          <PreviewPanel
            json={displayedPreviewJSON}
            onRefresh={handleRefreshPreview}
            title="Row Filtering - Preview"
          />
        </Grid>

        {/* Data Table */}
      </Grid>

      {/* Bottom Action Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={refiltering}
          color="secondary"
          disabled={!validationResult.valid}
        >
          REFILTER ROWS
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateNewDefinitionRowFiltering;
